import { GridColDef } from '@mui/x-data-grid';
import { ReactComponent as FileIcon } from 'assets/download-icon.svg';
import { Typography, Button, Stack, Box } from '@mui/material';
import MuiDataGrid from 'components/MuiDataGrid';
import { IUpedateSkillVideo } from 'apis/survey/type';
import { TGender } from 'apis/account/type';
import useI18n from 'hooks/useI18n';
import { useLocation } from 'react-router-dom';

interface Props {
  list: IUpedateSkillVideo[];
  isLoading: boolean;
  totalCount: number;
  page: number;
  onPageChange: (page: number) => void;
  onSelected: (value: string[]) => void;
  selected: string[];
}

function ContentsBody(props: Props) {
  const i18n = useI18n();
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const { list = [], isLoading, totalCount, page, onPageChange } = props;
  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  const handleDownloadClick = (url: string, videoUrl: string) => {
    fetch(url, {
      method: 'GET',
    })
      .then(res => {
        return res.blob();
      })
      .then(blob => {
        const blobURL = URL.createObjectURL(blob);
        const aTag = document.createElement('a');
        aTag.href = blobURL;
        aTag.download = videoUrl || 'download';
        document.body.appendChild(aTag);
        aTag.click();
        document.body.removeChild(aTag);
        URL.revokeObjectURL(blobURL);
      })
      .catch(e => {
        console.error(e);
      });
  };

  const renderVideoCell = (
    videoUrl: string,
    videoName: string,
    date: string
  ) => {
    if (!videoUrl) {
      return <Typography>-</Typography>;
    }
    return (
      <Stack direction="row" alignItems="center" spacing={1}>
        <Button
          sx={{
            minWidth: 'auto',
            padding: '5px',
            backgroundColor: '#fff',
          }}
          onClick={() => videoUrl && handleDownloadClick(videoUrl, videoName)}
        >
          <FileIcon />
        </Button>
        <Typography
          sx={{
            fontSize: '13px',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {videoName || 'No Name'}
        </Typography>
        <Typography
          sx={{
            fontSize: '13px',
            whiteSpace: 'nowrap',
            color: 'text.secondary',
          }}
        >
          {formatDate(date)}
        </Typography>
      </Stack>
    );
  };

  const columns: GridColDef[] = [
    { field: 'student_id', headerName: '아이디(이메일)', flex: 1 },
    { field: 'student_name', headerName: '이름', flex: 1 },
    { field: 'student_no', headerName: '학번/사번', flex: 1 },
    {
      field: 'student_gender',
      headerName: '성별',
      flex: 0.7,
      valueFormatter: ({ value }: { value: TGender }) =>
        i18n(`GENDER.${value}`),
    },
    {
      field: 'student_video.nursery_video1',
      headerName: 'Level 1',
      flex: 2,
      renderCell: ({ row }) =>
        renderVideoCell(
          row.student_video?.nursery_video1,
          row.student_video?.video_name1,
          row.student_video?.video_date1
        ),
    },
    {
      field: 'student_video.nursery_video2',
      headerName: 'Level 2',
      flex: 2,
      renderCell: ({ row }) =>
        renderVideoCell(
          row.student_video?.nursery_video2,
          row.student_video?.video_name2,
          row.student_video?.video_date2
        ),
    },
    {
      field: 'student_video.nursery_video3',
      headerName: 'Level 3',
      flex: 2,
      renderCell: ({ row }) =>
        renderVideoCell(
          row.student_video?.nursery_video3,
          row.student_video?.video_name3,
          row.student_video?.video_date3
        ),
    },
  ];

  const pageSize = Number(params.get('size')) || 20;

  return (
    <Box>
      <Typography
        variant="subtitle2"
        sx={{
          mb: 1,
          fontWeight: 500,
        }}
      >
        총 {totalCount}명
      </Typography>
      <MuiDataGrid
        columns={columns}
        loading={isLoading}
        onPageChange={onPageChange}
        page={page}
        pageSize={pageSize}
        rows={
          list && Array.isArray(list)
            ? list.map(item => ({ ...item, id: item.student_uuid }))
            : []
        }
        rowCount={totalCount}
      />
    </Box>
  );
}

export default ContentsBody;
